import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { IconClose } from "../../components/Icons";
import { useAuth } from "../../context/AuthContext";
import {
  BROKER_STATUS_LABELS,
  BROKER_STATUS_STYLES,
  BrokerDetailResponse,
} from "../../types/brokers";
import BrokerEmployeesTab from "./components/BrokerEmployeesTab";
import BrokerInvoicesTab from "./components/BrokerInvoicesTab";
import BrokerOverviewTab from "./components/BrokerOverviewTab";
import EditBrokerDrawer from "./components/EditBrokerDrawer";

type Tab = "overview" | "employees" | "invoices";

const TAB_LABELS: Record<Tab, string> = {
  overview: "Resumen",
  employees: "Empleados",
  invoices: "Facturas",
};

export default function BrokerDetail() {
  const { brokerId } = useParams<{ brokerId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState<BrokerDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    if (!brokerId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await request<BrokerDetailResponse>(
        `/api/admin/operations/brokers/${brokerId}`,
        { token },
      );
      setData(response);
    } catch (err) {
      if (err instanceof HttpError && err.status === 404) {
        setError("Broker no encontrado");
      } else {
        setError(
          err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [brokerId, token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ padding: "1.5rem", color: "#73879C" }}>Cargando…</div>
    );
  }
  if (error || !data) {
    return (
      <div style={{ padding: "1.5rem" }}>
        <div
          style={{
            background: "#fde2e2",
            color: "#b32424",
            padding: "0.6rem 0.8rem",
            borderRadius: "0.4rem",
            fontSize: ".85rem",
          }}
        >
          {error ?? "Sin datos"}
        </div>
        <div style={{ marginTop: "1rem" }}>
          <Link to="/brokers" style={{ color: "#0069d9", fontWeight: 600 }}>
            ← Volver a brokers
          </Link>
        </div>
      </div>
    );
  }

  const { broker, stats } = data;
  const statusStyle = BROKER_STATUS_STYLES[broker.status];

  return (
    <div style={{ padding: "1.5rem" }}>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "white",
          borderRadius: "0.6rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.06)",
          padding: "0.9rem 1.1rem",
          marginBottom: "0.75rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.95rem" }}>
          <div
            aria-hidden
            style={{
              width: "2.6rem",
              height: "2.6rem",
              borderRadius: "0.55rem",
              background: broker.themeColor || "#0069d9",
              color: "white",
              fontSize: ".95rem",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {(broker.brokerName.trim()[0] ?? "?").toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <h1
                style={{
                  margin: 0,
                  fontSize: "1.15rem",
                  fontWeight: 600,
                  color: "#1f2933",
                }}
              >
                {broker.brokerName}
              </h1>
              <span
                style={{
                  display: "inline-block",
                  padding: "0.15rem 0.5rem",
                  borderRadius: "0.4rem",
                  fontSize: ".7rem",
                  fontWeight: 600,
                  background: statusStyle.bg,
                  color: statusStyle.fg,
                  border: `1px solid ${statusStyle.border}`,
                }}
              >
                {BROKER_STATUS_LABELS[broker.status]}
              </span>
              {broker.markets.map((m) => (
                <span
                  key={m}
                  style={{
                    background: "#e9ecef",
                    color: "#495057",
                    fontSize: ".68rem",
                    fontWeight: 600,
                    padding: "0.1rem 0.4rem",
                    borderRadius: "0.35rem",
                    fontFamily: "monospace",
                  }}
                >
                  {m}
                </span>
              ))}
            </div>
            <div
              style={{
                marginTop: ".25rem",
                fontSize: ".82rem",
                color: "#73879C",
                display: "flex",
                gap: "0.85rem",
                flexWrap: "wrap",
              }}
            >
              <span>
                Broker{" "}
                <strong style={{ color: "#1f2933" }}>
                  #{broker.brokerInternalId}
                </strong>
              </span>
              {broker.brokerEmail && (
                <a
                  href={`mailto:${broker.brokerEmail}`}
                  style={{ color: "#0069d9", textDecoration: "none" }}
                >
                  {broker.brokerEmail}
                </a>
              )}
              {broker.brokerPhone && (
                <a
                  href={`tel:${broker.brokerPhone}`}
                  style={{ color: "#0069d9", textDecoration: "none" }}
                >
                  {broker.brokerPhone}
                </a>
              )}
              {broker.brokerIdCard && (
                <span style={{ fontFamily: "monospace" }}>
                  CIF {broker.brokerIdCard}
                </span>
              )}
              {broker.brokerIdDgsfp && (
                <span style={{ fontFamily: "monospace" }}>
                  DGSFP {broker.brokerIdDgsfp}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            title="Editar broker"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.4rem 0.85rem",
              borderRadius: "0.45rem",
              background: "#0069d9",
              color: "white",
              border: "1px solid #0069d9",
              cursor: "pointer",
              flexShrink: 0,
              fontSize: ".78rem",
              fontWeight: 600,
            }}
          >
            Editar
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            title="Volver"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "2rem",
              height: "2rem",
              padding: 0,
              borderRadius: "0.45rem",
              background: "#f8f9fb",
              color: "#1f2933",
              border: "1px solid #dee2e6",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <IconClose />
          </button>
        </div>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "0.6rem",
          marginBottom: "1rem",
        }}
      >
        <StatCard label="Empleados" value={`${stats.employeesEnabled} / ${stats.employeesTotal}`} hint="activos / total" />
        <StatCard label="Leads" value={stats.leadsTotal.toLocaleString("es-ES")} />
        <StatCard label="Completados" value={stats.leadsComplete.toLocaleString("es-ES")} tone="success" />
        <StatCard label="Cancelados" value={stats.leadsCanceled.toLocaleString("es-ES")} tone="danger" />
        <StatCard label="Facturas" value={stats.invoiceDocsTotal.toLocaleString("es-ES")} />
        {broker.medorCode && (
          <StatCard label="MEDOR" value={broker.medorCode} hint="código AXA" />
        )}
      </div>

      <div
        style={{
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #e9ecef",
            background: "#f8f9fb",
          }}
        >
          {(Object.keys(TAB_LABELS) as Tab[]).map((t) => {
            const active = tab === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                style={{
                  padding: "0.6rem 1.1rem",
                  fontSize: ".82rem",
                  fontWeight: 600,
                  background: active ? "white" : "transparent",
                  color: active ? "#0069d9" : "#73879C",
                  border: "none",
                  borderBottom: active
                    ? "2px solid #0069d9"
                    : "2px solid transparent",
                  cursor: "pointer",
                }}
              >
                {TAB_LABELS[t]}
              </button>
            );
          })}
        </div>

        <div style={{ padding: "1rem 1.2rem" }}>
          {tab === "overview" && <BrokerOverviewTab broker={broker} />}
          {tab === "employees" && (
            <BrokerEmployeesTab
              brokerInternalId={broker.brokerInternalId}
              token={token}
            />
          )}
          {tab === "invoices" && (
            <BrokerInvoicesTab
              brokerInternalId={broker.brokerInternalId}
              token={token}
            />
          )}
        </div>
      </div>

      {editOpen && (
        <EditBrokerDrawer
          broker={broker}
          token={token}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "success" | "danger";
}) {
  const accent =
    tone === "success" ? "#1a7f37" : tone === "danger" ? "#b32424" : "#0069d9";
  return (
    <div
      style={{
        background: "white",
        borderRadius: "0.5rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        padding: "0.55rem 0.75rem",
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <div
        style={{
          fontSize: ".7rem",
          textTransform: "uppercase",
          color: "#73879C",
          fontWeight: 700,
          letterSpacing: ".3px",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1f2933" }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: ".7rem", color: "#73879C" }}>{hint}</div>
      )}
    </div>
  );
}
